import React, { useState, useEffect } from 'react';
import { RefreshCw, CheckCircle, AlertCircle, Clock } from 'lucide-react';
import api from '../services/api';

const ImageSyncStatus = ({ commercantId }) => {
  const [syncStatus, setSyncStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [lastCheck, setLastCheck] = useState(null);

  const fetchSyncStatus = async () => {
    if (!commercantId) return;
    
    try {
      setLoading(true);
      const response = await api.request(`/cloudinary/sync-status/${commercantId}`);
      setSyncStatus(response);
      setLastCheck(new Date());
    } catch (error) {
      console.error('Erreur récupération statut sync:', error);
    } finally {
      setLoading(false);
    }
  };

  const forceSync = async () => {
    if (!commercantId) return;
    
    try {
      setLoading(true);
      const result = await api.request(`/cloudinary/force-sync/${commercantId}`, {
        method: 'POST'
      });
      const newTimestamp = result?.timestamp || new Date().toISOString();
      setSyncStatus((prev) => {
        if (!prev) return {
          commercantId,
          totalProduits: result?.produitsSynchronises ?? 0,
          produitsAvecImages: prev?.produitsAvecImages ?? 0,
          imagesTotal: result?.imagesTotal ?? 0,
          derniereModification: newTimestamp
        };
        return {
          ...prev,
          derniereModification: newTimestamp
        };
      });
      setLastCheck(new Date());
      await fetchSyncStatus(); // Recharger le statut après sync
    } catch (error) {
      console.error('Erreur synchronisation forcée:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSyncStatus();
    
    // Vérifier le statut toutes les 30 secondes
    const interval = setInterval(fetchSyncStatus, 30000);
    
    return () => clearInterval(interval);
  }, [commercantId]);

  if (!syncStatus) {
    return (
      <div className="bg-gray-50 rounded-lg p-4">
        <div className="flex items-center space-x-2">
          <Clock className="h-4 w-4 text-gray-400" />
          <span className="text-sm text-gray-600">Chargement du statut de synchronisation...</span>
        </div>
      </div>
    );
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'Jamais';
    return new Date(dateString).toLocaleString('fr-FR');
  };

  const getStatusIcon = () => {
    if (loading) {
      return <RefreshCw className="h-4 w-4 text-blue-500 animate-spin" />;
    }
    
    // Utiliser lastCheck (dernière vérification) au lieu de derniereModification
    const timeSinceLastCheck = lastCheck 
      ? new Date() - new Date(lastCheck)
      : Infinity;
    
    if (timeSinceLastCheck < 5 * 60 * 1000) { // Moins de 5 minutes
      return <CheckCircle className="h-4 w-4 text-green-500" />;
    } else if (timeSinceLastCheck < 30 * 60 * 1000) { // Moins de 30 minutes
      return <Clock className="h-4 w-4 text-yellow-500" />;
    } else {
      return <AlertCircle className="h-4 w-4 text-red-500" />;
    }
  };

  const getStatusText = () => {
    if (loading) return 'Synchronisation...';
    
    // Utiliser lastCheck (dernière vérification) au lieu de derniereModification
    const timeSinceLastCheck = lastCheck 
      ? new Date() - new Date(lastCheck)
      : Infinity;
    
    if (timeSinceLastCheck < 5 * 60 * 1000) {
      return 'Synchronisé récemment';
    } else if (timeSinceLastCheck < 30 * 60 * 1000) {
      return 'Synchronisation ancienne';
    } else {
      return 'Synchronisation requise';
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-medium text-gray-900">Synchronisation des images</h3>
        <button
          onClick={forceSync}
          disabled={loading}
          className="inline-flex items-center px-2 py-1 text-xs font-medium text-blue-600 bg-blue-50 rounded hover:bg-blue-100 disabled:opacity-50"
        >
          <RefreshCw className={`h-3 w-3 mr-1 ${loading ? 'animate-spin' : ''}`} />
          Forcer sync
        </button>
      </div>
      
      <div className="space-y-2">
        <div className="flex items-center space-x-2">
          {getStatusIcon()}
          <span className="text-sm text-gray-600">{getStatusText()}</span>
        </div>
        
        <div className="grid grid-cols-2 gap-4 text-xs text-gray-500">
          <div>
            <span className="font-medium">Produits:</span> {syncStatus.totalProduits}
          </div>
          <div>
            <span className="font-medium">Avec images:</span> {syncStatus.produitsAvecImages}
          </div>
          <div>
            <span className="font-medium">Images total:</span> {syncStatus.imagesTotal}
          </div>
          <div>
            <span className="font-medium">Dernière modif:</span> {formatDate(syncStatus.derniereModification)}
          </div>
        </div>
        
        {lastCheck && (
          <div className="text-xs text-gray-400 pt-2 border-t">
            Dernière vérification: {formatDate(lastCheck)}
          </div>
        )}
      </div>
    </div>
  );
};

export default ImageSyncStatus;
